import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  InputAdornment,
  alpha,
  useTheme,
  ListSubheader,
} from "@mui/material";
import {
  PersonOutlined,
  LanguageOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { updateAppPreferences } from "../../../../redux/actions/userAction";
import { updateUserProfileSettings } from "../../../../redux/actions/authAction";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { backendClient } from "../../../../api/backend";
import ReactGA from "react-ga4";
import { tracker } from "../../../../analytics";
import SectionHeader from "../SectionHeader/SectionHeader";
import { useUserTypes } from "../../../../hooks/useUserTypes";

const GeneralTab = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const user = useSelector((state) => state.auth?.user?.user);
  const appPreferences = useSelector((state) => state.user?.appPreferences);
  const { groups: userTypeGroups } = useUserTypes();

  const [language, setLanguage] = useState(currentLang);
  const [countries, setCountries] = useState([]);

  // Editable profile fields
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [phoneNumber, setPhoneNumber] = useState(user?.phone_number || "");
  const [userType, setUserType] = useState(user?.user_type || "");
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || "");
      setLastName(user.last_name || "");
      setPhoneNumber(user.phone_number || "");
      setUserType(user.user_type || "");
    }
  }, [user]);

  // Password reset states
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  useEffect(() => {
    if (appPreferences) {
      setLanguage(appPreferences.app_language);
      setCountries(appPreferences.countries || []);
    }
  }, [appPreferences]);

  const handleLanguageChange = async (event) => {
    const newLanguage = event.target.value;
    setLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);

    try {
      await dispatch(
        updateAppPreferences({
          app_language: newLanguage,
          search_language: newLanguage,
          countries: countries || [],
        })
      );

      enqueueSnackbar(
        t("language_changed_message", {
          language:
            newLanguage === "fr"
              ? t("language_french")
              : newLanguage === "ar"
              ? t("language_arabic")
              : newLanguage === "de"
              ? t("language_german")
              : newLanguage === "it"
              ? t("language_italian")
              : t("language_english"),
        }),
        { variant: "info" }
      );

      ReactGA.event({
        category: "Settings",
        action: "Language Changed",
        label: `New Language: ${newLanguage}`,
      });
      tracker.event({
        category: "Settings",
        action: "Language Changed",
        label: `New Language: ${newLanguage}`,
      });
    } catch (error) {
      console.error("Failed to update language preference:", error);
      enqueueSnackbar(t("error_saving"), { variant: "error" });
      setLanguage(currentLang);
      i18n.changeLanguage(currentLang);
    }
  };

  const profileHasChanges =
    firstName !== (user?.first_name || "") ||
    lastName !== (user?.last_name || "") ||
    phoneNumber !== (user?.phone_number || "") ||
    userType !== (user?.user_type || "");

  const handleSaveProfile = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      enqueueSnackbar(t("onboarding_field_required"), { variant: "error" });
      return;
    }
    setIsSavingProfile(true);
    try {
      await dispatch(
        updateUserProfileSettings({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone_number: phoneNumber.trim(),
          user_type: userType,
        })
      );
      enqueueSnackbar(t("profile_updated_success"), { variant: "success" });
      ReactGA.event({
        category: "Settings",
        action: "Profile Updated",
        label: `User ID: ${user.id}`,
      });
      tracker.event({
        category: "Settings",
        action: "Profile Updated",
        label: `User ID: ${user.id}`,
      });
    } catch (error) {
      console.error("Failed to update profile:", error);
      enqueueSnackbar(t("profile_update_error"), { variant: "error" });
    }
    setIsSavingProfile(false);
  };

  const validatePasswordReset = () => {
    const errors = {};

    if (!currentPassword) {
      errors.currentPassword = t("current_password_required");
    }

    if (!newPassword) {
      errors.newPassword = t("new_password_required");
    } else if (newPassword.length < 8 || !/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/\d/.test(newPassword)) {
      errors.newPassword = t("auth_password_requirements");
    }

    if (!confirmPassword) {
      errors.confirmPassword = t("confirm_password_required");
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = t("passwords_dont_match");
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleResetPassword = async () => {
    if (!validatePasswordReset()) return;

    setIsResettingPassword(true);

    try {
      await backendClient.resetUserPassword(user.id, currentPassword, newPassword);

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      enqueueSnackbar(t("password_reset_success"), { variant: "success" });

      ReactGA.event({
        category: "Settings",
        action: "Password Reset",
        label: `User ID: ${user.id}`,
      });
      tracker.event({
        category: "Settings",
        action: "Password Reset",
        label: `User ID: ${user.id}`,
      });
    } catch (error) {
      console.error("Failed to reset password:", error);
      enqueueSnackbar(
        error.response?.data?.detail || t("password_reset_error"),
        { variant: "error" }
      );
    }

    setIsResettingPassword(false);
  };

  return (
    <Box>
      {/* User Information Section */}
      <Card
        elevation={0}
        sx={{
          mb: 4,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
          overflow: "visible",
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <SectionHeader icon={<PersonOutlined />} title={t("user_information")} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("last_name")}
                variant="outlined"
                fullWidth
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                InputProps={{
                  sx: {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.divider,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.dark,
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t("first_name")}
                variant="outlined"
                fullWidth
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                InputProps={{
                  sx: {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.divider,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.dark,
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                disabled
                label={t("email")}
                variant="outlined"
                fullWidth
                value={user?.email || ""}
                InputProps={{
                  readOnly: true,
                  sx: {
                    bgcolor: theme.palette.action.hover,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.divider,
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t("phone_number")}
                variant="outlined"
                fullWidth
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                InputProps={{
                  sx: {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.divider,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.dark,
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>{t("user_type")}</InputLabel>
                <Select
                  value={userType}
                  label={t("user_type")}
                  onChange={(e) => setUserType(e.target.value)}
                  renderValue={(val) => {
                    for (const g of userTypeGroups) {
                      const found = g.items.find((i) => i.value === val);
                      if (found) return found.label;
                    }
                    return val;
                  }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.divider,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.dark,
                    },
                  }}
                >
                  {userTypeGroups.map((group) => [
                    <ListSubheader
                      key={group.key}
                      sx={{
                        bgcolor: theme.palette.background.paper,
                        color: theme.palette.primary.main,
                        fontSize: '10px',
                        fontWeight: 600,
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        lineHeight: 1,
                        pt: 2,
                        pb: 1,
                      }}
                    >
                      {group.label}
                    </ListSubheader>,
                    ...group.items.map((item) => (
                      <MenuItem key={item.value} value={item.value} sx={{ pl: 3 }}>
                        {item.label}
                      </MenuItem>
                    )),
                  ])}
                </Select>
              </FormControl>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "center", mt: 1 }}
            >
              <Button
                variant="contained"
                disableElevation
                disabled={!profileHasChanges || !firstName.trim() || !lastName.trim()}
                onClick={handleSaveProfile}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.mode === "dark" ? "#000" : "#fff",
                  px: 4,
                  py: 1.5,
                  borderRadius: "10px",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": { bgcolor: theme.palette.primary.dark },
                  "&.Mui-disabled": {
                    bgcolor: alpha(theme.palette.primary.main, 0.4),
                    color: theme.palette.mode === "dark" ? "#000" : "#fff",
                  },
                }}
              >
                {isSavingProfile ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        border: "3px solid rgba(255,255,255,0.3)",
                        borderTopColor: "#fff",
                        animation: "spin 1s linear infinite",
                        marginRight: 8,
                      }}
                    ></span>
                    {t("updating")}...
                  </Box>
                ) : (
                  t("save_changes")
                )}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Language Settings Section */}
      <Card
        elevation={0}
        sx={{
          mb: 4,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <SectionHeader
            icon={<LanguageOutlined />}
            title={t("language_settings")}
            description={t("language_settings_description")}
          />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: theme.palette.background.paper,
                  p: 3,
                  borderRadius: "12px",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1,
                  }}
                >
                  {t("app_language")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, mb: 2 }}
                >
                  {t("app_language_description")}
                </Typography>
                <FormControl fullWidth variant="outlined" sx={{ mt: "auto" }}>
                  <InputLabel id="app-language-select-label">
                    {t("app_language")}
                  </InputLabel>
                  <Select
                    labelId="app-language-select-label"
                    value={language}
                    label={t("app_language")}
                    onChange={handleLanguageChange}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.divider,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.primary.dark,
                      },
                    }}
                  >
                    <MenuItem value="fr">🇫🇷 {t("language_french")}</MenuItem>
                    <MenuItem value="ar">🇲🇦 {t("language_arabic")}</MenuItem>
                    <MenuItem value="de">🇩🇪 {t("language_german")}</MenuItem>
                    <MenuItem value="it">🇮🇹 {t("language_italian")}</MenuItem>
                    <MenuItem value="en">🇺🇸 {t("language_english")}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Password Reset Section */}
      <Card
        elevation={0}
        sx={{
          mb: 4,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <SectionHeader
            icon={<LockOutlined />}
            title={t("reset_password")}
            description={t("reset_password_description")}
          />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label={t("current_password")}
                type={showCurrentPassword ? "text" : "password"}
                variant="outlined"
                fullWidth
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                error={!!passwordErrors.currentPassword}
                helperText={passwordErrors.currentPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowCurrentPassword(!showCurrentPassword)
                        }
                        edge="end"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.divider,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.dark,
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("new_password")}
                type={showNewPassword ? "text" : "password"}
                variant="outlined"
                fullWidth
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                error={!!passwordErrors.newPassword}
                helperText={passwordErrors.newPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.divider,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.dark,
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("confirm_password")}
                type={showNewPassword ? "text" : "password"}
                variant="outlined"
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={!!passwordErrors.confirmPassword}
                helperText={passwordErrors.confirmPassword}
                InputProps={{
                  sx: {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.divider,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.primary.dark,
                    },
                  },
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "center", mt: 2 }}
            >
              <Button
                variant="contained"
                disableElevation
                disabled={!currentPassword || !newPassword || !confirmPassword}
                onClick={handleResetPassword}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.mode === "dark" ? "#000" : "#fff",
                  px: 4,
                  py: 1.5,
                  borderRadius: "10px",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": { bgcolor: theme.palette.primary.dark },
                  "&.Mui-disabled": {
                    bgcolor: alpha(theme.palette.primary.main, 0.4),
                    color: theme.palette.mode === "dark" ? "#000" : "#fff",
                  },
                }}
              >
                {isResettingPassword ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <span
                      style={{
                        display: "inline-block",
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        border: "3px solid rgba(255,255,255,0.3)",
                        borderTopColor: "#fff",
                        animation: "spin 1s linear infinite",
                        marginRight: 8,
                      }}
                    ></span>
                    {t("updating")}...
                  </Box>
                ) : (
                  t("reset_password_button")
                )}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GeneralTab;
